import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, CardMedia } from '@mui/material';
import { IProduct, useUpdateProductEmailInfoMutation } from '../../../generated/graphql';
import EditButton from '../../Common/Buttons/EditButton';
import { AppContext } from '../../../AppContext';
import { Types } from '../../../enums/TypeEnums';
import { canUserEdit } from '../../../utils/rightUtils';
import { Actions } from '../../../enums/ActionEnums';
import { Colors } from '../../../utils/theme';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import InputComponent from '../../Common/Fields/InputComponent';
import { getLabelColor } from '../../../utils/styleUtils';
import EmailBannerUploader, { ImageContainerMargins } from '../EmailBannerUploader';

interface IProps {
  product: IProduct;
}

const imageContainerDimensions: ImageContainerMargins = {
  height: '210px',
  backupMarginTop: 42
};

const ProductEmailTab: React.FunctionComponent<IProps> = ({ product }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [editable, setEditable] = useState(false);
  const [subject, setSubject] = useState(product.email_subject);
  const [emailBanner, setEmailBanner] = useState(product.email_banner);
  const [emailBody, setEmailBody] = useState(product.email_body || '');
  const [updateProduct, { data, loading }] = useUpdateProductEmailInfoMutation();
  const canEdit = canUserEdit(state.user, Types.Product);

  useEffect(() => {
    if (data && data.result) {
      setEditable(false);
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          text: `Product ${data.result.name} updated`,
          severity: 'success'
        }
      });
    }
  }, [data]);

  const handleCancel = () => {
    setSubject(product.email_subject);
    setEmailBanner(product.email_banner);
    setEditable(false);
    setEmailBody(product.email_body || '');
  };

  const submitForm = () => {
    updateProduct({
      variables: {
        id: parseInt(product.id),
        input: {
          email_subject: subject ? subject.trim() : null,
          email_body: emailBody ? emailBody.trim() : null,
          email_banner: emailBanner ? emailBanner.trim() : null
        }
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message.slice(15),
          autoHide: 6000
        }
      })
    );
  };

  return (
    <Box sx={{ pt: '150px', px: '12%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-general-edit-button"
          onClick={() => setEditable(true)}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: '10%'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
          <InputComponent
            id="product-email-subject"
            label="Email Subject"
            value={subject || ''}
            onChange={(e) => setSubject(e.currentTarget.value)}
            disabled={!editable || loading}
          />
          <Box id="product-email-banner-container">
            <Box id="product-email-banner-header">
              <label style={{ color: getLabelColor(editable) }}>Email Banner</label>
              {editable && emailBanner && (
                <Button
                  id="product-clear-banner"
                  style={{ width: 'auto', float: 'right', marginTop: '-10px' }}
                  onClick={() => setEmailBanner(null)}
                  disabled={!emailBanner || loading}
                >
                  Clear
                </Button>
              )}
            </Box>
            {emailBanner ? (
              <CardMedia
                style={{ maxHeight: imageContainerDimensions.height, marginTop: '12px' }}
                component="img"
                src={emailBanner as string}
              />
            ) : (
              <EmailBannerUploader
                editable={editable}
                loading={loading}
                setImage={setEmailBanner}
                dimensions={imageContainerDimensions}
              />
            )}
          </Box>
        </Box>
        <InputComponent
          id="product-email-body"
          label="Email body"
          value={emailBody}
          onChange={(e) => setEmailBody(e.currentTarget.value)}
          multiline
          rows={14}
          disabled={!editable || loading}
        />
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '60px' }}>
          <CancelButton
            id="product-email-cancel-button"
            disabled={loading}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="product-email-save-button"
            disabled={loading}
            onClick={submitForm}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductEmailTab;
